import { useMemo, useState, useEffect } from "react";
import {
  ArrowLeft,
  Info,
  Activity,
  Cloud,
  Network,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  Check,
  ArrowUpRight,
  ArrowDownRight,
  AlertTriangle,
  Lightbulb,
  UserPlus,
  UserMinus,
  Link2,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Eye,
  Sparkles,
} from "lucide-react";
import { SiGooglecloud, SiAmazon } from "react-icons/si";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import { useToast } from "../../../hooks/use-toast";
import { NGROK } from "../../../APIs";
import { myLocalStorage } from "../../StorageHelper";
import axios from "axios";
import { useQuery } from "react-query";
import CloudRecentActivity from "./CloudRecentActivity";
import CloudCorrelations from "./CloudCorrelations";
import { getAnalyticsApis } from "./helper";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../../ui/common-ui/hover-card";
import { CloudAnomalyAlert } from "../CloudSightAlerts/types/alert";
import { AlertGrouping } from "../CloudSightAlerts/components/alerts/AlertGrouping";
import { AlertDetails } from "../CloudSightAlerts/components/alerts/AlertDetails";
import { analyzeEntity, InteractiveAreaChart, SaaSIcon } from "../Cloud/statusHelpers";
import "./CloudEntityAnalytics.css";

type CloudProvider = "gcp" | "aws" | "azure";
type EntityType = "user" | "nhi";

interface CloudEntityAnalyticsProps {
  cloudProvider: CloudProvider;
  entityId: string;
  entityType: EntityType;
  projectId: string;
  roles: any[];
  syncedAt: any;
  email: string;
  entityName: string;
  cloudNHIType: string | "";
  details: any;
  extrasdetails: any;
}

const cloudConfig = {
  gcp: {
    name: "GCP",
    icon: SiGooglecloud,
    color: "text-blue-600",
  },
  aws: {
    name: "AWS",
    icon: SiAmazon,
    color: "text-orange-600",
  },
  azure: {
    name: "Azure",
    icon: Cloud,
    color: "text-blue-500",
  },
};

const DONUT_COLORS = [
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#8B5CF6",
  "#EC4899",
  "#F97316",
  "#06B6D4",
  "#84CC16",
  "#EAB308",
  "#A855F7",
];

const normalizeActor = (value: unknown) => String(value || "").trim();

const isServiceAccountActor = (actor: string) => {
  const normalized = actor.toLowerCase();
  return (
    normalized.includes("gserviceaccount.com") ||
    normalized.includes("serviceaccount:") ||
    normalized.includes("managed identity") ||
    normalized.includes("assumed-role") ||
    normalized.includes("role/")
  );
};

const deriveActorName = (log: any) => {
  const raw = log?.rawLog || {};
  const protoPayload = raw?.protoPayload || {};
  const authenticationInfo = protoPayload?.authentication_info || {};
  const requestMetadata = protoPayload?.request_metadata || {};
  const userIdentity = raw?.userIdentity || {};

  return normalizeActor(
    authenticationInfo?.principal_email ||
    userIdentity?.arn ||
    userIdentity?.userName ||
    userIdentity?.principalId ||
    raw?.identity ||
    raw?.actor ||
    log?.actor ||
    log?.entity ||
    requestMetadata?.caller_supplied_user_agent
  );
};

const aggregateActors = (logs: any[], actorType: "user" | "service-account") => {
  const counts = new Map<string, number>();

  logs.forEach((log) => {
    const actor = deriveActorName(log);
    if (!actor) return;

    const serviceAccount = isServiceAccountActor(actor);
    if (actorType === "service-account" && !serviceAccount) return;
    if (actorType === "user" && serviceAccount) return;

    counts.set(actor, (counts.get(actor) || 0) + 1);
  });

  return Array.from(counts.entries())
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10);
};

const flattenRoleSource = (source: any): any[] => {
  if (!source) return [];

  if (Array.isArray(source)) {
    return source.flatMap((item) => flattenRoleSource(item));
  }

  if (typeof source === "string") {
    return source
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (
    typeof source === "object" &&
    (
      source?.roleName ||
      source?.roleArn ||
      source?.roleARN ||
      source?.arn ||
      source?.title ||
      source?.role ||
      source?.roleId ||
      source?.roleDefinitionName
    )
  ) {
    return [source];
  }

  return [];
};

const normalizeRoleLabel = (role: any) => {
  if (!role) return "";

  if (typeof role === "string") {
    return role.trim();
  }

  return String(
    role?.roleName ||
    role?.title ||
    role?.role ||
    role?.roleDefinitionName ||
    role?.roleId ||
    role?.roleArn ||
    role?.roleARN ||
    role?.arn ||
    "",
  ).trim();
};

const extractDisplayRoles = (...sources: any[]) =>
  Array.from(
    new Set(
      sources
        .flatMap((source) => flattenRoleSource(source))
        .map((role) => normalizeRoleLabel(role))
        .filter(Boolean),
    ),
  );

const parseDateValue = (value: any) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "number") {
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  const normalizedValue = String(value || "").trim();

  if (
    !normalizedValue ||
    normalizedValue === "-" ||
    normalizedValue.toLowerCase() === "n/a"
  ) {
    return null;
  }

  const protobufSeconds = normalizedValue.match(/seconds:\s*(\d+)/)?.[1];
  const protobufNanos = normalizedValue.match(/nanos:\s*(\d+)/)?.[1];

  if (protobufSeconds) {
    const parsedDate = new Date(
      Number(protobufSeconds) * 1000 + Number(protobufNanos || 0) / 1e6,
    );
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  const nativeDate = new Date(normalizedValue);

  if (!Number.isNaN(nativeDate.getTime())) {
    return nativeDate;
  }

  const dayFirstMatch = normalizedValue.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,\s*|\s+)?(\d{1,2})(?::(\d{2}))(?::(\d{2}))?(?:\s*(AM|PM))?$/i,
  );

  if (dayFirstMatch) {
    const [, day, month, year, hours, minutes, seconds, meridiem] =
      dayFirstMatch;
    let normalizedHours = Number(hours);

    if (meridiem) {
      const normalizedMeridiem = meridiem.toUpperCase();
      if (normalizedMeridiem === "PM" && normalizedHours < 12) {
        normalizedHours += 12;
      }
      if (normalizedMeridiem === "AM" && normalizedHours === 12) {
        normalizedHours = 0;
      }
    }

    const parsedDate = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      normalizedHours,
      Number(minutes),
      Number(seconds || 0),
    );

    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  const dateOnlyMatch = normalizedValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);

  if (dateOnlyMatch) {
    const [, day, month, year] = dateOnlyMatch;
    const parsedDate = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
    );

    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  return null;
};

const formatDisplayDate = (value: any) => {
  const parsedDate = parseDateValue(value);
  return parsedDate ? parsedDate.toLocaleString("en-GB") : "";
};

export default function CloudEntityAnalytics({
  cloudProvider,
  entityId,
  entityType,
  projectId,
  roles,
  syncedAt,
  email,
  entityName,
  cloudNHIType,
  details = {},
  extrasdetails = {},
}: CloudEntityAnalyticsProps) {



  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState<"overview" | "details" | "activity" | "correlations">("overview");
  const [detachDialogOpen, setDetachDialogOpen] = useState(false);
  const [selectedDetach, setSelectedDetach] = useState<{
    type: "resolve" | "suggest";
    role: string;
    resource?: string;
  } | null>(null);
  const [resolutionPage, setResolutionPage] = useState(1);
  const [suggestionPage, setSuggestionPage] = useState(1);
  const ITEMS_PER_PAGE = 5;

  const [groupingMode, setGroupingMode] = useState<"alertType" | "alert" | "entity">("alert");
  const [selectedAlert, setSelectedAlert] = useState<CloudAnomalyAlert | null>(null);
  const [isAlertDetailsOpen, setIsAlertDetailsOpen] = useState(false);
  const [alertSearchValue, setAlertSearchValue] = useState("");
  const [alertSeverityFilter, setAlertSeverityFilter] = useState<string[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const pageEl = document.querySelector('.cea-page');
    if (pageEl && pageEl.parentElement) {
      pageEl.parentElement.scrollTo(0, 0);
    }
  }, []);


  const analyzed = useMemo(() => {
    return analyzeEntity({
      key: email || entityId,
      name: entityName,
      subtitle: email,
      roles: roles,
      activity: 0,
      type: entityType,
      raw: details
    });
  }, [email, entityId, entityName, roles, entityType, details]);

  const triggeredDelta = analyzed.snapshotMetrics[analyzed.snapshotMetrics.length - 1].triggered - analyzed.snapshotMetrics[0].triggered;
  const triggeredColor = analyzed.status === "improved" ? "hsl(142, 55%, 48%)" : "hsl(0, 62%, 55%)";
  const corrColor = "hsl(220, 65%, 55%)";
  const corrValues = analyzed.snapshotMetrics.map((_, i) => analyzed.correlations.totalRelations + (i % 4) - 1);
  const corrDelta = corrValues[corrValues.length - 1] - corrValues[0];

  const latestTenant =
    details?.tenantName ||
    details?.wsTenantName ||
    myLocalStorage.getItem("latestTenant")?.tenantName ||
    "";

  const CloudIcon = cloudConfig[cloudProvider]?.icon ?? Cloud;
  const latestSubListRaw = myLocalStorage.getItem("latestSubList");
  const latestSubList = latestSubListRaw ? JSON.parse(latestSubListRaw) : null;
  const gcpProjectId = myLocalStorage.getItem("gcpSelectedProjectId");
  const accountId = myLocalStorage.getItem("selectedAwsAccount");
  const subscriptionId = latestSubList?.azureSubscriptionId;
  const resolvedProjectId =
    cloudProvider.toUpperCase() === "GCP"
      ? gcpProjectId || projectId
      : cloudProvider.toUpperCase() === "AZURE"
        ? subscriptionId || projectId
        : accountId || projectId;
  const normalizedCloudNhiType = String(cloudNHIType || "").toUpperCase();
  const gcpServiceAccountEmail =
    details?.serviceAccountEmail ||
    details?.item?.serviceAccountEmail ||
    details?.item?.email ||
    details?.sa?.serviceAccountEmail ||
    details?.sa?.email ||
    email ||
    entityId ||
    "";
  const shouldFetchGcpNhiRoles =
    cloudProvider === "gcp" &&
    entityType === "nhi" &&
    ![
      "SECRET",
      "TOKEN",
      "K8SERVICE",
      "K8TOKEN",
      "K8SECRET",
      "OIDC",
    ].includes(normalizedCloudNhiType) &&
    Boolean(latestTenant && resolvedProjectId && gcpServiceAccountEmail);

  const {
    resourceApi,
    resolutionApi,
    permissionApi,
    suggestionApi,
    rawLogsAPI,
    secretAnalyticsApi,
  } = getAnalyticsApis({
    entityType,
    cloudProvider,
    latestTenant,
    projectId: resolvedProjectId,
    email: entityId,
    cloudNHIType,
    details,
    extrasdetails,
  });

  const {
    data: gcpNhiRolesMap,
    isLoading: isGcpNhiRolesLoading,
    isFetching: isGcpNhiRolesFetching,
    isFetched: hasFetchedGcpNhiRoles,
  } = useQuery(
    ["gcp-nhi-roles", latestTenant, resolvedProjectId],
    async () => {
      const response = await axios.get(
        `${NGROK}/api/gcp/get-all-non-human-identities-roles`,
        {
          params: {
            tenantName: latestTenant,
            projectId: resolvedProjectId,
          },
        },
      );

      return response.data || {};
    },
    {
      enabled: shouldFetchGcpNhiRoles,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );


  const { data: resourceUsage = {} } = useQuery({
    queryKey: [
      "user-resource-usage",
      cloudProvider,
      entityId,
      resolvedProjectId,
      latestTenant,
    ],
    queryFn: async () => {
      const { data } = await axios.get(resourceApi);
      return data || {};
    },
    enabled: !!resourceApi,
  });

  const { data: permissionUsage = {} } = useQuery({
    queryKey: [
      "user-permission-usage",
      cloudProvider,
      entityId,
      resolvedProjectId,
      latestTenant,
    ],
    queryFn: async () => {
      const { data } = await axios.get(permissionApi);
      return data || {};
    },
    enabled: !!permissionApi,
  });

  const { data: resolutions = {} } = useQuery({
    queryKey: [
      "user-analytics-resolutions",
      cloudProvider,
      entityId,
      resolvedProjectId,
      latestTenant,
    ],
    queryFn: async () => {
      const { data } = await axios.get(resolutionApi);
      return data || {};
    },
    enabled: !!resolutionApi,
  });

  const { data: suggestions = {} } = useQuery({
    queryKey: [
      "user-analytics-suggestions",
      cloudProvider,
      entityId,
      resolvedProjectId,
      latestTenant,
    ],
    queryFn: async () => {
      const { data } = await axios.get(suggestionApi);
      return data || {};
    },
    enabled: !!suggestionApi,
  });

  const { data: gcpSecretAnalytics = {} } = useQuery({
    queryKey: [
      "gcp-secret-analytics",
      cloudProvider,
      entityId,
      resolvedProjectId,
      latestTenant,
    ],
    queryFn: async () => {
      const { data } = await axios.get(secretAnalyticsApi!);
      return data || {};
    },
    enabled: !!secretAnalyticsApi,
  });

  const { data: secretRawLogs = [] } = useQuery({
    queryKey: [
      "secret-raw-logs",
      cloudProvider,
      entityId,
      resolvedProjectId,
      latestTenant,
    ],
    queryFn: async () => {
      if (!rawLogsAPI) return [];
      const { data } = await axios.get(rawLogsAPI);
      return Array.isArray(data) ? data : [];
    },
    enabled: !!rawLogsAPI && (cloudNHIType === "SECRET" || cloudNHIType === "K8SECRET"),
  });

  const { data: rawAlerts = [], isLoading: isAlertsLoading } = useQuery({
    queryKey: [
      "entity-alerts",
      cloudProvider,
      resolvedProjectId,
      latestTenant,
    ],
    queryFn: async () => {
      const response = await axios.get(`${NGROK}/api/cloud/get-anomalies-group`, {
        params: {
          tenantName: latestTenant,
          cloudId: resolvedProjectId,
          cloudType: cloudProvider?.toUpperCase(),
          timeRangeCode: 7,
          groupBy: "alertType",
        },
      });
      const groupRaw = response.data || [];
      let flattened: CloudAnomalyAlert[] = [];
      if (Array.isArray(groupRaw)) {
        flattened = groupRaw;
      } else {
        Object.values(groupRaw).forEach((value) => {
          if (Array.isArray(value)) flattened = flattened.concat(value as CloudAnomalyAlert[]);
        });
      }
      return flattened;
    },
    enabled: !!latestTenant && !!resolvedProjectId && (activeTab === "details" || activeTab === "overview"),
  });

  const entityResolutions = resolutions as any;
  const entitySuggestions = suggestions as any;

  const analyticsResourceUsage =
    secretAnalyticsApi && cloudNHIType === "SECRET"
      ? (gcpSecretAnalytics as any)?.topResources || {}
      : resourceUsage;

  const analyticsPermissionUsage =
    secretAnalyticsApi && cloudNHIType === "SECRET"
      ? (gcpSecretAnalytics as any)?.topPermissions || {}
      : permissionUsage;

  const totalResourceUsage = Object.values(analyticsResourceUsage).reduce(
    (sum: number, val) => sum + Number(val),
    0
  );

  const totalPermissionUsage = Object.values(analyticsPermissionUsage).reduce(
    (sum: number, val) => sum + Number(val),
    0
  );

  const topResources = Object.entries(analyticsResourceUsage)
    .sort(([, a], [, b]) => Number(b) - Number(a))
    .slice(0, 10);

  const topPermissions = Object.entries(analyticsPermissionUsage)
    .sort(([, a], [, b]) => Number(b) - Number(a))
    .slice(0, 10);

  const isSecretAnalytics =
    entityType === "nhi" &&
    (cloudNHIType === "SECRET" || cloudNHIType === "K8SECRET");

  const topSecretUsers = useMemo(
    () =>
      secretAnalyticsApi && cloudNHIType === "SECRET"
        ? Object.entries((gcpSecretAnalytics as any)?.topUsers || {}).sort(([, a], [, b]) => Number(b) - Number(a)).slice(0, 10)
        : aggregateActors(secretRawLogs as any[], "user"),
    [secretRawLogs, secretAnalyticsApi, gcpSecretAnalytics, cloudNHIType]
  );

  const topSecretServiceAccounts = useMemo(
    () =>
      secretAnalyticsApi && cloudNHIType === "SECRET"
        ? Object.entries((gcpSecretAnalytics as any)?.topServiceAccounts || {}).sort(([, a], [, b]) => Number(b) - Number(a)).slice(0, 10)
        : aggregateActors(secretRawLogs as any[], "service-account"),
    [secretRawLogs, secretAnalyticsApi, gcpSecretAnalytics, cloudNHIType]
  );

  const resolutionItems = Object.entries(
    entityResolutions.resolutions || {}
  ).map(([currentRole, resources]) => ({
    currentRole,
    resourceMappings: Object.entries(
      resources as Record<string, string[]>
    ).map(([resource, suggestedRoles]) => ({
      resource,
      suggestedRoles: Array.isArray(suggestedRoles)
        ? suggestedRoles
        : [suggestedRoles],
    })),
  }));


  const suggestionItems = Object.entries(
    entitySuggestions.suggestions || {}
  ).map(([level, role]) => ({
    level: String(level),
    role: String(role),
  }));

  const totalResolutionPages = Math.ceil(
    resolutionItems.length / ITEMS_PER_PAGE
  );
  const totalSuggestionPages = Math.ceil(
    suggestionItems.length / ITEMS_PER_PAGE
  );

  const paginatedResolutionItems = resolutionItems.slice(
    (resolutionPage - 1) * ITEMS_PER_PAGE,
    resolutionPage * ITEMS_PER_PAGE
  );
  const paginatedSuggestionItems = suggestionItems.slice(
    (suggestionPage - 1) * ITEMS_PER_PAGE,
    suggestionPage * ITEMS_PER_PAGE
  );

  const {
    cloudType,
    secretDetails,
    tokenDetails,
    cloudTokenDetails,
    serviceDetails,
    serviceAccountEmail,
    principalId,
    secretName,
    oidcARN,
    oidc,
  } = details;
  const displayRoles = useMemo(
    () =>
      extractDisplayRoles(
        roles,
        details?.roles,
        details?.serviceroles,
        details?.serviceRoles,
        details?.roleDefinitions,
        details?.roleNames,
        details?.roleDetails,
        details?.iamRoleDetails,
        details?.serviceDetails,
        details?.serviceDetails?.roles,
        details?.serviceDetails?.serviceroles,
        details?.serviceDetails?.serviceRoles,
        details?.serviceDetails?.roleDefinitions,
        details?.serviceDetails?.roleDetails,
        details?.serviceDetails?.iamRoleDetails,
        details?.item,
        details?.item?.roles,
        details?.item?.serviceroles,
        details?.item?.serviceRoles,
        details?.item?.roleDefinitions,
        details?.item?.roleNames,
        details?.item?.roleDetails,
        details?.item?.iamRoleDetails,
        details?.item?.serviceDetails,
        details?.item?.serviceDetails?.roles,
        details?.item?.serviceDetails?.serviceroles,
        details?.item?.serviceDetails?.serviceRoles,
        details?.item?.serviceDetails?.roleDefinitions,
        details?.item?.serviceDetails?.roleDetails,
        details?.item?.serviceDetails?.iamRoleDetails,
        details?.sa,
        details?.sa?.roles,
        details?.sa?.serviceroles,
        details?.sa?.serviceRoles,
        details?.sa?.roleDefinitions,
        details?.sa?.roleNames,
        details?.sa?.roleDetails,
        details?.sa?.iamRoleDetails,
        details?.sa?.serviceDetails,
        details?.sa?.serviceDetails?.roles,
        details?.sa?.serviceDetails?.serviceroles,
        details?.sa?.serviceDetails?.serviceRoles,
        details?.sa?.serviceDetails?.roleDefinitions,
        details?.sa?.serviceDetails?.roleDetails,
        details?.sa?.serviceDetails?.iamRoleDetails,
        extrasdetails?.roles,
        extrasdetails?.serviceroles,
        extrasdetails?.serviceRoles,
        extrasdetails?.roleDefinitions,
        extrasdetails?.roleNames,
        extrasdetails?.roleDetails,
        extrasdetails?.iamRoleDetails,
        extrasdetails?.serviceDetails,
        extrasdetails?.serviceDetails?.roles,
        extrasdetails?.serviceDetails?.serviceroles,
        extrasdetails?.serviceDetails?.serviceRoles,
        extrasdetails?.serviceDetails?.roleDefinitions,
        extrasdetails?.serviceDetails?.roleDetails,
        extrasdetails?.serviceDetails?.iamRoleDetails,
      ),
    [details, extrasdetails, roles],
  );
  const gcpFetchedRoles = useMemo(
    () => {
      if (!shouldFetchGcpNhiRoles || !gcpNhiRolesMap) {
        return [];
      }

      const roleSources = [
        gcpNhiRolesMap?.[gcpServiceAccountEmail],
        gcpNhiRolesMap?.[details?.serviceAccountEmail],
        gcpNhiRolesMap?.[serviceAccountEmail],
        gcpNhiRolesMap?.[email],
        gcpNhiRolesMap?.[entityId],
      ];

      return extractDisplayRoles(...roleSources);
    },
    [
      details?.serviceAccountEmail,
      email,
      entityId,
      gcpNhiRolesMap,
      gcpServiceAccountEmail,
      serviceAccountEmail,
      shouldFetchGcpNhiRoles,
    ],
  );
  const shouldDelayGcpRoleDisplay =
    shouldFetchGcpNhiRoles &&
    !hasFetchedGcpNhiRoles &&
    (isGcpNhiRolesLoading || isGcpNhiRolesFetching);
  const effectiveDisplayRoles =
    shouldDelayGcpRoleDisplay
      ? []
      : gcpFetchedRoles.length > 0
        ? gcpFetchedRoles
        : displayRoles;
  const rolesDisplayText = shouldDelayGcpRoleDisplay
    ? "Loading roles..."
    : effectiveDisplayRoles.length > 0
      ? effectiveDisplayRoles.slice(0, 2).join(", ")
      : "No roles assigned";
  const formattedSyncedAt = useMemo(
    () =>
      formatDisplayDate(
        syncedAt ||
        details?.syncedAt ||
        details?.item?.syncedAt ||
        details?.item?.serviceDetails?.syncedAt ||
        details?.serviceDetails?.syncedAt ||
        details?.sa?.syncedAt ||
        details?.sa?.serviceDetails?.syncedAt ||
        extrasdetails?.syncedAt ||
        details?.createdAt ||
        details?.item?.createDate ||
        details?.item?.createdAt ||
        details?.item?.serviceDetails?.createDate ||
        details?.serviceDetails?.createDate ||
        details?.sa?.createDate ||
        details?.sa?.serviceDetails?.createDate ||
        extrasdetails?.createDate,
      ),
    [details, extrasdetails, syncedAt],
  );

  const convertProtobufTimestamp = (str: any) => {
    try {
      const seconds = parseInt(str?.match(/seconds:\s*(\d+)/)?.[1] || 0);
      const nanos = parseInt(str?.match(/nanos:\s*(\d+)/)?.[1] || 0);
      return new Date(seconds * 1000 + nanos / 1e6).toLocaleString("en-GB");
    } catch {
      return "Invalid date";
    }
  };

  const handleDetach = (
    type: "resolve" | "suggest",
    role: string,
    resource?: string
  ) => {
    setSelectedDetach({ type, role, resource });
    setDetachDialogOpen(true);
  };

  const handleDetachConfirm = () => {
    toast({
      title: "Role detached",
      description: `Successfully detached ${selectedDetach?.role}`,
    });
    setDetachDialogOpen(false);
    setSelectedDetach(null);
  };

  const getHeaderMetaItems = () => {
    switch (cloudNHIType) {
      case "SECRET":
        return [
          {
            label: cloudProvider.toUpperCase() === "AWS" ? "Secret ARN" : "Secret Name",
            value:
              cloudProvider.toUpperCase() === "AWS"
                ? secretName
                : extrasdetails?.displayName || extrasdetails?.name,
          },
          {
            label: "Owner",
            value:
              extrasdetails?.owner ||
              extrasdetails?.projectId ||
              extrasdetails?.tenantName ||
              entityName,
          },
          {
            label: "Created",
            value: extrasdetails?.createdAt ||
              convertProtobufTimestamp(extrasdetails?.createTime),
          },
          {
            label: "Replication",
            value: extrasdetails?.replicationType || "-",
          },
        ];

      case "TOKEN":
        return [
          {
            label: "Service Account",
            value: extrasdetails?.serviceAccountEmail || serviceAccountEmail,
          },
          {
            label: "Key ID",
            value: cloudTokenDetails?.keyId || extrasdetails?.keyId,
          },
          {
            label: "Valid After",
            value: extrasdetails?.validAfter
              ? new Date(extrasdetails.validAfter).toLocaleString("en-GB")
              : "-",
          },
          {
            label: "Managed By",
            value: extrasdetails?.isManagedKey ? "Google Managed" : "User Managed",
          },
        ];

      case "K8SERVICE":
      case "K8TOKEN":
      case "K8SECRET": {
        const item =
          cloudNHIType === "K8SERVICE"
            ? serviceDetails
            : cloudNHIType === "K8TOKEN"
              ? tokenDetails
              : secretDetails;

        return [
          { label: "Name", value: item?.name },
          { label: "Namespace", value: item?.namespace },
          { label: "Cluster", value: item?.clusterId },
          {
            label: "Last Activity",
            value: item?.lastActivity || item?.syncedAt || "-",
          },
        ];
      }

      case "OIDC":
        return [
          { label: "URL", value: oidc?.url },
          { label: "Provider ARN", value: oidc?.providerArn || "-" },
          { label: "Client Id", value: oidc?.clientIds?.join(", ") || "-" },
          {
            label: "Created At",
            value: oidc?.createDate
              ? new Date(oidc.createDate).toLocaleString("en-GB")
              : "-",
          },
        ];

      default:
        return [
          {
            label: cloudProvider.toUpperCase() === "AWS" ? "Role Name" : "Account Email",
            value: serviceAccountEmail || getEntityName(),
          },
          {
            label: "Roles",
            value: rolesDisplayText,
          },
          {
            label: "Synced At",
            value: formattedSyncedAt || "-",
          },
          {
            label: cloudProvider.toUpperCase() === "AZURE" ? "Subscription" : cloudProvider.toUpperCase() === "AWS" ? "Account" : "Project",
            value: entityName,
          },
        ];
    }
  };

  const getEntityName = () => {
    switch (cloudNHIType) {
      case "SECRET":
        // AWS uses secret ARN, others use displayName
        return cloudProvider?.toUpperCase() === "AWS"
          ? secretName
          : extrasdetails?.displayName || extrasdetails?.name;

      case "TOKEN":
        return extrasdetails?.serviceAccountEmail;

      case "K8SERVICE":
      case "K8TOKEN":
      case "K8SECRET":
        const item =
          cloudNHIType === "K8SERVICE"
            ? serviceDetails
            : cloudNHIType === "K8TOKEN"
              ? tokenDetails
              : secretDetails;
        return item?.name;

      case "OIDC":
        return oidc?.url || oidc?.providerArn || oidc?.clientIds?.[0];

      default:
        // Fallback → email or service account email
        return (
          serviceAccountEmail ||
          extrasdetails?.serviceAccountEmail ||
          "-"
        );
    }
  };


  const getSectionTitle = () => {
    switch (cloudNHIType) {
      case "SECRET":
        return "Secret Details";
      case "TOKEN":
        return "Token Details";
      case "K8SERVICE":
        return "Kubernetes Service Account Details";
      case "K8TOKEN":
        return "Kubernetes Token Details";
      case "K8SECRET":
        return "Kubernetes Secret Details";
      case "OIDC":
        return "OIDC Details";
      default:
        return "Service Account Details";
    }
  };
  const isValidDate = Boolean(formattedSyncedAt);
  const headerMetaItems = getHeaderMetaItems().filter((item) => item?.value !== undefined);
  const correlationIdentifiers = Array.from(
    new Set(
      [
        entityId,
        email,
        entityName,
        getEntityName(),
        serviceAccountEmail,
        principalId,
        secretName,
        cloudTokenDetails?.keyId,
        extrasdetails?.displayName,
        extrasdetails?.name,
        oidcARN,
        oidc?.url,
      ].filter(Boolean)
    )
  ) as string[];

  const entityAlerts = useMemo(() => {
    return rawAlerts.filter((alert: CloudAnomalyAlert) =>
      correlationIdentifiers.some((id) =>
        alert.entity?.toLowerCase().includes(id.toLowerCase())
      )
    );
  }, [rawAlerts, correlationIdentifiers]);

  const renderDistributionCard = ({
    title,
    description,
    items,
    total,
    emptyLabel = "No data found",
  }: {
    title: string;
    description: string;
    items: [string, number][];
    total: number;
    emptyLabel?: string;
  }) => (
    <Card>
      <CardHeader className="border-b pb-4">
        <CardTitle className="text-base font-medium">
          {title}
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          {description}
        </p>
      </CardHeader>
      <CardContent className="py-8">
        <div className="flex flex-col items-center gap-6">
          <div className="relative w-56 h-56">
            <svg
              viewBox="0 0 200 200"
              className="transform -rotate-90"
            >
              {items.map(([name, count], idx) => {
                const percentage =
                  total > 0 ? (Number(count) / total) * 100 : 0;

                const prevPercentages = items
                  .slice(0, idx)
                  .reduce(
                    (sum, [, c]) =>
                      sum +
                      (total > 0 ? (Number(c) / total) * 100 : 0),
                    0
                  );

                const circumference = 2 * Math.PI * 70;
                const offset =
                  circumference *
                  (1 - percentage / 100);
                const rotation =
                  (prevPercentages / 100) * 360;

                return (
                  <circle
                    key={String(name)}
                    cx="100"
                    cy="100"
                    r="70"
                    fill="none"
                    stroke={DONUT_COLORS[idx]}
                    strokeWidth="32"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    style={{
                      transformOrigin: "100px 100px",
                      transform: `rotate(${rotation}deg)`,
                    }}
                  />
                );
              })}
            </svg>

            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                {total !== 0 ? (
                  <>
                    <div className="text-2xl font-bold">
                      {total.toLocaleString()}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Total Events
                    </div>
                  </>
                ) : (
                  <div className="text-xs text-muted-foreground mt-1">
                    {emptyLabel}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="w-full space-y-2.5">
            {items.length === 0 && (
              <div className="text-sm text-muted-foreground text-center">
                {emptyLabel}
              </div>
            )}
            {items.map(([name, count], idx) => {
              const percentage =
                total > 0 ? (Number(count) / total) * 100 : 0;

              return (
                <div
                  key={String(name)}
                  className="flex items-center gap-3 group cursor-default"
                  title={`${Number(
                    count
                  ).toLocaleString()} events`}
                >
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{
                      backgroundColor:
                        DONUT_COLORS[idx],
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate font-medium">
                      {String(name)}
                    </div>
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">
                      {Number(count).toLocaleString()}
                    </span>
                    <span className="text-sm font-semibold">
                      {percentage.toFixed(1)}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );


  const statusLabel = analyzed.status === "improved" ? "Risk Reduced" : analyzed.status === "worsened" ? "Risk Increased" : "Stable";

  return (
    <div className="cea-page">
      {/* ── Header ── */}
      <div className="cea-header">
        <div className="cea-header-top">
          <button className="cea-back-btn" onClick={() => window.history.back()} data-testid="button-back">
            <ArrowLeft />
            Back
          </button>
          <div className="cea-sync-meta">
            {isValidDate && <span className="cea-sync-label">Synced: {formattedSyncedAt}</span>}
            <span className="cea-sync-label">
              {cloudType?.toUpperCase() === "AWS" ? "Account" : cloudType?.toUpperCase() === "AZURE" ? "Subscription" : "Project"}: {entityName}
            </span>
          </div>
        </div>

        <div className="cea-entity-row">
          <div className="cea-avatar">
            <CloudIcon className={`w-6 h-6 ${(cloudConfig[cloudProvider]?.color) ?? "text-gray-400"}`} />
            <div className={`cea-avatar-status ${analyzed.status}`} />
          </div>

          <div className="cea-entity-info">
            <div className="cea-entity-name">
              {entityType === "nhi" ? getEntityName() : cloudProvider === "aws" ? `${email} (${entityId || "-"})` : email}
            </div>
            <div className="cea-entity-sub">
              <span className="cea-badge provider">{cloudProvider.toUpperCase()}</span>
              <span className={`cea-badge status ${analyzed.status}`}>
                {analyzed.status === "improved" ? <TrendingDown className="w-3 h-3" /> : analyzed.status === "worsened" ? <TrendingUp className="w-3 h-3" /> : null}
                {statusLabel}
              </span>
              {cloudNHIType === "SECRET" && (
                <span className="cea-badge provider">{extrasdetails?.isManagedSecret ? "Managed" : "User Managed"}</span>
              )}
            </div>

            {entityType !== "nhi" && (
              <div className="cea-roles-row">
                <span className="cea-roles-label">Roles:</span>
                {shouldDelayGcpRoleDisplay ? (
                  <span className="cea-role-chip">Loading…</span>
                ) : effectiveDisplayRoles.length > 0 ? (
                  <>
                    {effectiveDisplayRoles.slice(0, 4).map((role, idx) => (
                      <span key={idx} className="cea-role-chip">{role || "Unknown"}</span>
                    ))}
                    {effectiveDisplayRoles.length > 4 && (
                      <span className="cea-role-chip">+{effectiveDisplayRoles.length - 4} more</span>
                    )}
                  </>
                ) : (
                  <span className="cea-role-chip">No roles</span>
                )}
              </div>
            )}

            {entityType === "nhi" && headerMetaItems.length > 0 && (
              <div className="cea-meta-grid">
                {headerMetaItems.map((item) => (
                  <div key={item.label} className="cea-meta-item">
                    <div className="cea-meta-label">{item.label}</div>
                    <div className="cea-meta-value">{item.value || "–"}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>



      {/* ── Tab Bar ── */}
      <div className="cea-tabs">
        <button className={`cea-tab ${activeTab === "overview" ? "active" : ""}`} onClick={() => setActiveTab("overview")} data-testid="tab-overview">
          <Activity />
          Overview
        </button>
        <button className={`cea-tab ${activeTab === "details" ? "active" : ""}`} onClick={() => setActiveTab("details")} data-testid="tab-details">
          <Info />
          Details
        </button>
        <button className={`cea-tab ${activeTab === "activity" ? "active" : ""}`} onClick={() => setActiveTab("activity")} data-testid="tab-activity">
          <Activity />
          Recent Activity
        </button>
        <button className={`cea-tab ${activeTab === "correlations" ? "active" : ""}`} onClick={() => setActiveTab("correlations")} data-testid="tab-correlations">
          <Network />
          Correlations
        </button>
      </div>


      {/* ── OVERVIEW TAB ── */}
      {activeTab === "overview" && (() => {
        return (
          <div className="cea-content">
            <div className="cea-overview">

              {/* Entity Alerts Section — live API */}
              <Card className="cea-risk-panel">
                <CardHeader className="border-b">
                  <div>
                    <CardTitle className="text-lg font-semibold">
                      Entity Security Alerts
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Security alerts associated with this entity
                    </p>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  {isAlertsLoading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="h-[180px] bg-muted/40 animate-pulse rounded-xl border border-border/50" />
                      ))}
                    </div>
                  ) : entityAlerts.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground">
                      No alerts found for this entity
                    </div>
                  ) : (
                    <AlertGrouping
                      alerts={entityAlerts}
                      onViewAlertDetails={(alert) => {
                        setSelectedAlert(alert);
                        setIsAlertDetailsOpen(true);
                      }}
                      searchValue={alertSearchValue}
                      onSearchChange={setAlertSearchValue}
                      severityFilter={alertSeverityFilter}
                      onSeverityFilterChange={setAlertSeverityFilter}
                      groupingMode={groupingMode}
                      onGroupingModeChange={setGroupingMode}
                      hideEntityGroup
                      hideSearch
                    />
                  )}
                </CardContent>
              </Card>

              {/* Charts Row — coming soon */}
              <div className="cea-charts-row">
                {[
                  { label: "Alerts Triggered", sub: "Last 7 Days" },
                  { label: "SaaS Correlations", sub: "Last 7 Days" },
                ].map(({ label, sub }) => (
                  <div key={label} className="cea-chart-card">
                    <div className="cea-chart-header">
                      <div>
                        <div className="cea-chart-label">{label}</div>
                        <div className="cea-chart-sub">{sub}</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-center h-[120px] text-muted-foreground text-sm opacity-50 select-none">
                      No data available
                    </div>
                  </div>
                ))}
              </div>

              {/* Metrics Grid — coming soon */}
              <div className="cea-metrics-grid">
                {/* Potential Risk */}
                <div className="cea-risk-panel">
                  <div className="cea-risk-header">
                    <div className="cea-risk-header-left">
                      <div className="cea-risk-icon-wrap"><AlertTriangle /></div>
                      <span className="cea-risk-title">Potential Risk</span>
                    </div>
                    <div className="cea-risk-actions">
                      <button className="cea-risk-btn" onClick={() => setActiveTab("details")}>
                        <BarChart3 /> Alert Breakdown
                      </button>
                      <button className="cea-risk-btn" onClick={() => setActiveTab("activity")}>
                        <Eye /> View Graph
                      </button>
                    </div>
                  </div>
                  <div className="cea-risk-body">
                    <div className="flex items-center justify-center py-6 text-muted-foreground text-sm opacity-50 select-none">
                      No risk data available
                    </div>
                  </div>
                </div>

                {/* Insights */}
                <div className="cea-insights-panel">
                  <div className="cea-insights-header">
                    <div className="cea-insights-icon-wrap"><Lightbulb /></div>
                    <span className="cea-insights-title">Insights</span>
                    <Sparkles className="w-3.5 h-3.5 ml-0.5 opacity-60" style={{ color: "hsl(var(--primary))" }} />
                  </div>
                  <div className="cea-insights-body">
                    <div className="flex items-center justify-center py-6 text-muted-foreground text-sm opacity-50 select-none">
                      No insights available
                    </div>
                  </div>
                </div>

                {/* SaaS Correlations */}
                <div className="cea-corr-panel">
                  <div className="cea-corr-header">
                    <div className="cea-corr-header-left">
                      <div className="cea-corr-icon-wrap"><Link2 /></div>
                      <span className="cea-corr-title">SaaS Correlations</span>
                    </div>
                    <span className="cea-corr-count">0 apps</span>
                  </div>
                  <div className="flex items-center justify-center py-6 text-muted-foreground text-sm opacity-50 select-none">
                    No correlations found
                  </div>
                </div>

                {/* Role Changes */}
                <div className="cea-roles-panel">
                  <div className="cea-roles-panel-header">
                    <div className="cea-roles-icon-wrap"><UserPlus /></div>
                    <span className="cea-roles-panel-title">Role Changes</span>
                    <span className="cea-roles-panel-sub">Oldest → Latest</span>
                  </div>
                  <div className="cea-roles-body">
                    <div className="flex items-center justify-center py-6 text-muted-foreground text-sm opacity-50 select-none">
                      No role changes detected
                    </div>
                  </div>
                </div>

              </div>

            </div>
          </div>
        );
      })()}

      {/* DETAILS TAB */}
      {activeTab === "details" && (
        <div className="cea-content">
          <div className="space-y-6">
            {/* Top Resources + Top Permissions */}
            <div className="grid grid-cols-2 gap-6">
              {renderDistributionCard({
                title: "Top Resources",
                description: `Most accessed resources by ${entityType === "nhi" ? "service account" : "user"}`,
                items: topResources as [string, number][],
                total: totalResourceUsage,
              })}
              <Card>
                <CardHeader className="border-b pb-4">
                  <CardTitle className="text-base font-medium">
                    Top Permissions
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    Most frequently used permissions by{" "}
                    {entityType === "nhi"
                      ? "service account"
                      : "user"}
                  </p>
                </CardHeader>
                <CardContent className="py-6">
                  <div className="space-y-3">
                    {topPermissions.map(
                      ([permission, count]) => {
                        const percentage =
                          (Number(count) /
                            totalPermissionUsage) *
                          100;

                        return (
                          <div
                            key={String(permission)}
                            className="space-y-1.5 cursor-default"
                            title={`${Number(
                              count
                            ).toLocaleString()} uses`}
                          >
                            <div className="flex items-baseline justify-between gap-3">
                              <div className="text-sm font-medium font-mono truncate flex-1">
                                {String(permission)}
                              </div>
                              <div className="flex-shrink-0">
                                <span className="text-sm font-semibold">
                                  {percentage.toFixed(1)}%
                                </span>
                              </div>
                            </div>
                            <div className="bg-muted rounded-sm h-2 overflow-hidden">
                              <div
                                className="bg-primary h-full rounded-sm transition-all"
                                style={{
                                  width: `${percentage}%`,
                                }}
                              />
                            </div>
                          </div>
                        );
                      }
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {isSecretAnalytics && (
              <div className="grid grid-cols-2 gap-6">
                {renderDistributionCard({
                  title: "Top Users",
                  description: "Users accessing or interacting with this secret",
                  items: topSecretUsers as [string, number][],
                  total: topSecretUsers.reduce((sum, [, count]) => sum + Number(count), 0),
                  emptyLabel: "No user activity found",
                })}
                {renderDistributionCard({
                  title: "Top Service Accounts",
                  description: "Service accounts interacting with this secret",
                  items: topSecretServiceAccounts as [string, number][],
                  total: topSecretServiceAccounts.reduce((sum, [, count]) => sum + Number(count), 0),
                  emptyLabel: "No service account activity found",
                })}
              </div>
            )}


            {!isSecretAnalytics && (
              <Card>
                <CardHeader className="border-b">
                  <div>
                    <CardTitle className="text-lg font-semibold">
                      Resolve Project-Level Roles
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Recommended role replacements for improved
                      security and least-privilege access
                    </p>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="border rounded-md">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr className="border-b">
                          <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wide">
                            Current Role
                          </th>
                          <th className="text-center px-4 py-3 text-xs font-medium uppercase tracking-wide">
                            Resource → Recommended Roles
                          </th>
                          <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wide">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedResolutionItems.length === 0 ? (
                          <tr className="border-b last:border-b-0">
                            <td colSpan={3} className="text-center py-4 text-muted-foreground">
                              No resolutions found
                            </td>
                          </tr>
                        ) : (
                          paginatedResolutionItems.map((item, itemIdx) => {
                            const firstMapping = item.resourceMappings[0];
                            const remainingCount = item.resourceMappings.length - 1;

                            return (
                              <tr
                                key={itemIdx}
                                className="border-b last:border-b-0 h-14 hover:bg-muted/30"
                              >
                                {/* Current Role */}
                                <td className="px-4 py-3 w-[200px]">
                                  <Badge variant="destructive" className="font-mono text-xs">
                                    {item.currentRole}
                                  </Badge>
                                </td>

                                {/* Resource → Recommended Roles with HoverCard */}
                                <td className="px-4 py-3 text-center align-middle">
                                  <div className="flex items-center justify-center gap-3">
                                    <span className="font-medium text-sm">{firstMapping.resource}</span>
                                    <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                    <div className="flex flex-wrap justify-center gap-1">
                                      {firstMapping.suggestedRoles.map((role, roleIdx) => (
                                        <Badge key={roleIdx} variant="outline" className="text-green-600 border-green-600">
                                          {role}
                                        </Badge>
                                      ))}
                                    </div>

                                    {/* HoverCard only wraps the +X badge */}
                                    {remainingCount > 0 && (
                                      <HoverCard openDelay={100} closeDelay={100}>
                                        <HoverCardTrigger asChild>
                                          <span className="inline-flex cursor-pointer">
                                            <Badge variant="secondary" className="text-xs ml-2 hover:bg-muted">
                                              +{remainingCount}
                                            </Badge>
                                          </span>
                                        </HoverCardTrigger>
                                        <HoverCardContent
                                          className="w-auto min-w-[300px] max-w-[500px] p-4"
                                          align="center"
                                          side="bottom"
                                          sideOffset={8}
                                        >
                                          <div className="space-y-1">
                                            <p className="text-xs font-semibold uppercase text-muted-foreground mb-3">
                                              All Resource Mappings ({item.resourceMappings.length})
                                            </p>
                                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                              {item.resourceMappings.map(({ resource, suggestedRoles }, idx) => (
                                                <div
                                                  key={idx}
                                                  className="flex items-center gap-2 py-2 px-3 rounded-md bg-muted/30 border"
                                                >
                                                  <span className="font-medium text-sm whitespace-nowrap">
                                                    {resource}
                                                  </span>
                                                  <ArrowRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                                  <div className="flex flex-wrap gap-1">
                                                    {suggestedRoles.map((role, roleIdx) => (
                                                      <Badge
                                                        key={roleIdx}
                                                        variant="outline"
                                                        className="text-green-600 border-green-600 whitespace-nowrap"
                                                      >
                                                        {role}
                                                      </Badge>
                                                    ))}
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        </HoverCardContent>
                                      </HoverCard>
                                    )}
                                  </div>
                                </td>
                                {/* Action */}
                                <td className="px-4 py-3 text-right align-middle w-[120px]">
                                  <Button
                                    size="sm"
                                    onClick={() => handleDetach("resolve", item.currentRole)}
                                    className="gap-1"
                                  >
                                    <Check className="w-3 h-3" />
                                    Resolve
                                  </Button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>



                    </table>

                  </div>

                  {/* Pagination */}
                  {totalResolutionPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                      <p className="text-sm text-muted-foreground">
                        Showing{" "}
                        {(resolutionPage - 1) *
                          ITEMS_PER_PAGE +
                          1}{" "}
                        to{" "}
                        {Math.min(
                          resolutionPage *
                          ITEMS_PER_PAGE,
                          resolutionItems.length
                        )}{" "}
                        of {resolutionItems.length} items
                      </p>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setResolutionPage((p) =>
                              Math.max(1, p - 1)
                            )
                          }
                          disabled={resolutionPage === 1}
                          data-testid="button-prev-resolution"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Previous
                        </Button>

                        <div className="flex items-center gap-1">
                          {Array.from(
                            {
                              length: totalResolutionPages,
                            },
                            (_, i) => i + 1
                          ).map((page) => (
                            <Button
                              key={page}
                              variant={
                                page === resolutionPage
                                  ? "default"
                                  : "outline"
                              }
                              size="sm"
                              onClick={() =>
                                setResolutionPage(page)
                              }
                              className="w-9"
                              data-testid={`button-resolution-page-${page}`}
                            >
                              {page}
                            </Button>
                          ))}
                        </div>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setResolutionPage((p) =>
                              Math.min(
                                totalResolutionPages,
                                p + 1
                              )
                            )
                          }
                          disabled={
                            resolutionPage ===
                            totalResolutionPages
                          }
                          data-testid="button-next-resolution"
                        >
                          Next
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}


            {!isSecretAnalytics && (
              <Card>
                <CardHeader className="border-b">
                  <div>
                    <CardTitle className="text-lg font-semibold">
                      Suggested Role Cleanup
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Over-privileged or redundant project-level
                      roles that can be safely removed
                    </p>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="border rounded-md">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr className="border-b">
                          <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wide">
                            Scope
                          </th>
                          <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wide">
                            Role to Remove
                          </th>
                          <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wide">
                            Reason
                          </th>
                          <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wide">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedSuggestionItems.length === 0 ? <tr className="border-b last:border-b-0 hover-elevate">
                          <td colSpan={5} className="text-center px-4 py-3">No suggestions found</td>
                        </tr>
                          :

                          paginatedSuggestionItems.map(
                            (item, idx) => (
                              <tr
                                key={idx}
                                className="border-b last:border-b-0 hover-elevate"
                              >
                                <td className="px-4 py-3">
                                  <Badge
                                    variant="secondary"
                                    className="text-xs"
                                  >
                                    {item.level}
                                  </Badge>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="font-mono text-sm font-medium">
                                    {item.role}
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="text-sm text-muted-foreground">
                                    Over-privileged access
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() =>
                                      handleDetach(
                                        "suggest",
                                        item.role,
                                        item.level
                                      )
                                    }
                                    data-testid={`button-detach-suggest-${idx}`}
                                  >
                                    <Check className="w-3 h-3" />
                                    Detach
                                  </Button>
                                </td>
                              </tr>
                            )
                          )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalSuggestionPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                      <p className="text-sm text-muted-foreground">
                        Showing{" "}
                        {(suggestionPage - 1) *
                          ITEMS_PER_PAGE +
                          1}{" "}
                        to{" "}
                        {Math.min(
                          suggestionPage *
                          ITEMS_PER_PAGE,
                          suggestionItems.length
                        )}{" "}
                        of {suggestionItems.length} items
                      </p>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setSuggestionPage((p) =>
                              Math.max(1, p - 1)
                            )
                          }
                          disabled={suggestionPage === 1}
                          data-testid="button-prev-suggestion"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Previous
                        </Button>

                        <div className="flex items-center gap-1">
                          {Array.from(
                            {
                              length: totalSuggestionPages,
                            },
                            (_, i) => i + 1
                          ).map((page) => (
                            <Button
                              key={page}
                              variant={
                                page === suggestionPage
                                  ? "default"
                                  : "outline"
                              }
                              size="sm"
                              onClick={() =>
                                setSuggestionPage(page)
                              }
                              className="w-9"
                              data-testid={`button-suggestion-page-${page}`}
                            >
                              {page}
                            </Button>
                          ))}
                        </div>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setSuggestionPage((p) =>
                              Math.min(
                                totalSuggestionPages,
                                p + 1
                              )
                            )
                          }
                          disabled={
                            suggestionPage ===
                            totalSuggestionPages
                          }
                          data-testid="button-next-suggestion"
                        >
                          Next
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

          </div>
        </div>
      )}

      {/* ACTIVITY TAB */}
      {activeTab === "activity" && (
        <div className="cea-content">
          <CloudRecentActivity
            url={`${NGROK}/api/cloud/get-raw-logs?tenantName=${latestTenant}&cloudId=${resolvedProjectId}&entity=${entityId || getEntityName()}&cloudType=${cloudProvider.toUpperCase()}`}
            tenantName={latestTenant}
            cloudType={cloudProvider.toUpperCase()}
          />
        </div>
      )}

      {activeTab === "correlations" && (
        <div className="cea-content">
          <CloudCorrelations
            url={`${NGROK}/api/cloud/cloud-saas-correlations?tenantName=${encodeURIComponent(latestTenant)}&cloudId=${encodeURIComponent(resolvedProjectId || "")}`}
            entityIdentifiers={correlationIdentifiers}
          />
        </div>
      )}

      {/* DETACH CONFIRMATION */}
      <AlertDialog
        open={detachDialogOpen}
        onOpenChange={setDetachDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Detach Role</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to detach role{" "}
              <span className="font-medium text-foreground break-words max-w-[475px] block">
                {selectedDetach?.role}
              </span>
              {selectedDetach?.resource && (
                <>
                  {" "}
                  from resource{" "}
                  <span className="font-medium text-foreground">
                    {selectedDetach.resource}
                  </span>
                </>
              )}
              ?
              <br />
              <br />
              This action will immediately remove associated
              permissions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-detach">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDetachConfirm}
              className="bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700"
              data-testid="button-confirm-detach"
            >
              Detach Role
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDetails
        alert={selectedAlert}
        open={isAlertDetailsOpen}
        onClose={() => {
          setIsAlertDetailsOpen(false);
          setSelectedAlert(null);
        }}
      />
    </div>
  );
}
